import { styled } from "@mui/material/styles";
import { Button, CheckIcon, CloseIcon, ErrorIcon, QuestionIcon } from "@UI";
import React, { useCallback, useEffect, useRef } from "react";
export const HookCard = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-3)",
    padding: "var(--j-sp-3)",
    borderRadius: "var(--j-r-md)",
    background: "var(--j-surface)",
    border: "1px solid var(--j-line)",
    "&[data-tone=destructive] .hook-head > :first-child": {
        color: "var(--j-err)"
    },
    "& .hook-head": {
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--j-sp-2)"
    },
    "& .hook-head > .hook-text": {
        flex: 1,
        minWidth: 0
    },
    "& h4": {
        margin: 0,
        fontSize: "var(--j-fs-md)",
        lineHeight: "var(--j-lh-md)",
        fontWeight: 600,
        overflowWrap: "anywhere"
    },
    "& .hook-desc": {
        margin: "var(--j-sp-0-5) 0 0",
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-ink-2)"
    },
    "& .hook-error": {
        margin: 0,
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        color: "var(--j-err)"
    },
    "& .hook-actions": {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--j-sp-2)"
    },
    "& .hook-meta": {
        marginTop: "calc(-1 * var(--j-sp-1-5))",
        fontSize: "var(--j-fs-xs)",
        lineHeight: "var(--j-lh-xs)",
        color: "var(--j-ink-3)"
    },
    "& .hook-meta[data-tone=warn]": {
        color: "var(--j-warn)"
    }
});
export const HookTile = styled("span")({
    flexShrink: 0,
    marginTop: 1,
    display: "grid",
    placeItems: "center",
    color: "var(--j-ink-2)",
    "&[data-tone=warn]": {
        color: "var(--j-warn)"
    }
});
export const BannerAction = Button;
const Line = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--j-sp-1-5)",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)",
    "&[data-tone=error]": {
        color: "var(--j-err)"
    },
    "&:focus": {
        outline: "none"
    },
    "&:focus-visible": {
        outline: "var(--j-focus-w) solid var(--j-accent)",
        outlineOffset: 2,
        borderRadius: "var(--j-r-xs)"
    },
    "& > svg": {
        flexShrink: 0,
        marginTop: 1
    },
    "& > .settled-body": {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "var(--j-sp-0-5)",
        overflowWrap: "anywhere"
    }
});
const LINE_ICON = {
    ok: /*#__PURE__*/ React.createElement(CheckIcon, {
        size: 14,
        color: "var(--j-ok)"
    }),
    neutral: /*#__PURE__*/ React.createElement(CloseIcon, {
        size: 14
    }),
    error: /*#__PURE__*/ React.createElement(ErrorIcon, {
        size: 14
    }),
    muted: /*#__PURE__*/ React.createElement(ErrorIcon, {
        size: 14,
        color: "var(--j-ink-3)"
    }),
    wait: /*#__PURE__*/ React.createElement(QuestionIcon, {
        size: 14
    })
};
export function useCardFocus() {
    const card = useRef(null);
    const hadFocus = useRef(false);
    const cardRef = useCallback((element)=>{
        if (element) card.current = element;
        else hadFocus.current = Boolean(card.current?.contains(document.activeElement));
    }, []);
    return {
        cardRef,
        hadFocus
    };
}
export function SettledLine({ tone, hadFocus, focusIfLost, role, children }) {
    const lineRef = useRef(null);
    useEffect(()=>{
        if (focusIfLost && document.activeElement === document.body) lineRef.current?.focus();
        if (!hadFocus?.current) return;
        hadFocus.current = false;
        lineRef.current?.focus();
    }, [
        hadFocus
    ]);
    return /*#__PURE__*/ React.createElement(Line, {
        ref: lineRef,
        tabIndex: -1,
        "data-tone": tone,
        role: role
    }, LINE_ICON[tone], /*#__PURE__*/ React.createElement("div", {
        className: "settled-body"
    }, children));
}
